import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import priceLinkTemplateUrl from "/price_link_template.csv?url";
import { useAppSelector } from "../../../redux/hooks";
import { getOrganizationsOptions } from "../../admin/api/useOrganizations";
import { uploadToS3 } from "../../uploads/uploadToS3";
import { apiClient } from "../../../zodios/client";
import { Select, SelectItem } from "../../../components/fields/base/Select";
import FileDropZone from "../../../components/core/blocks/FileDropZone";
import { errorToast } from "../../../utils/toasts";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";

// Mirrors the backend row cap (being raised to 10k by the backend team). Keep in sync with it.
const MAX_ROWS = 10000;
const FILE_EXCEEDS_MAX_ROWS_MARKER = "FILE_EXCEEDS_MAX_ROWS";

export function UploadForm({ onUploadCreated }: { onUploadCreated: (uploadId: number) => void }) {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["pricingv2"]);

  const user = useAppSelector((state) => state.user);
  const { marketPlace } = useAppSelector((state) => state.marketPlace);

  const [followerOrgId, setFollowerOrgId] = useState<number | null>(null);

  const orgsQuery = useQuery({
    ...getOrganizationsOptions(),
    select: (data) => {
      const labelAccessor = user.prefers_org_name_display ? "name" : "profile_name";
      const nameCounts: Record<string, number> = {};
      data.forEach((org) => {
        nameCounts[org[labelAccessor]] = (nameCounts[org[labelAccessor]] ?? 0) + 1;
      });
      return data
        .filter((org) => {
          return org.id.toString() !== user.organization_id && org.marketplaces.includes(marketPlace);
        })
        .map((org) => {
          const base = `${org[labelAccessor]}`;
          const label = nameCounts[base]! > 1 ? `${base} (${org.profile_type ?? "other"})` : base;
          return { value: org.id, label };
        });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      if (followerOrgId === null) return;

      const lines = (await file.text()).split(/\r?\n/).filter((line) => line.trim());
      const rowCount = lines.length - 1;
      if (rowCount > MAX_ROWS) {
        throw new Error(`${FILE_EXCEEDS_MAX_ROWS_MARKER}:${rowCount}`);
      }

      const id = await uploadToS3({ file, uploadType: "price_link_upload" });
      await apiClient.processUpload(
        {
          marketplace: marketPlace,
          follower_org_id: followerOrgId,
          async_run: true,
          autoProcess: false,
        },
        { params: { id } },
      );
      void queryClient.invalidateQueries({ queryKey: [{ entity: "uploads" }] });
      return id;
    },
    onSuccess: (id) => {
      if (id !== undefined) onUploadCreated(id);
    },
    onError: (error) => {
      const message =
        error.message.startsWith(FILE_EXCEEDS_MAX_ROWS_MARKER) ?
          t(($) => $.pricingv2["File exceeds the maximum of {max} rows (found {count})."], {
            max: MAX_ROWS,
            count: Number(error.message.split(":")[1]),
          })
        : t(($) => $.pricingv2["An unexpected error occurred. Please try again."]);
      errorToast({
        title: t(($) => $.pricingv2["Upload failed"]),
        message,
        error,
      });
    },
  });

  const followerOrgOptions = useMemo(() => orgsQuery.data ?? [], [orgsQuery.data]);

  return (
    <VStack gap="medium" alignItems="stretch">
      <VStack gap="xsmall" alignItems="stretch">
        <Select
          label={t(($) => $.pricingv2["Follower account"])}
          selectedKey={followerOrgId}
          onSelectionChange={(key) => setFollowerOrgId(key === null ? null : Number(key))}
          items={followerOrgOptions}
          isDisabled={orgsQuery.isLoading || uploadMutation.isPending}
        >
          {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
        </Select>
        <span className={css({ fontSize: "small", color: "gray.600" })}>
          {t(
            ($) =>
              $.pricingv2[
                "Account that the follower SKUs belong to. Only accounts active in the current marketplace are available."
              ],
          )}
        </span>
      </VStack>
      <HStack gap="small" className={css({ fontSize: "small" })}>
        <span className={css({ color: "gray.600" })}>{t(($) => $.pricingv2["Download a template:"])}</span>
        <a href={priceLinkTemplateUrl} download>
          {t(($) => $.pricingv2["Price Link Template"])}
        </a>
      </HStack>

      <p className={css({ color: "gray.600", fontSize: "small" })}>
        {t(
          ($) =>
            $.pricingv2[
              "Leader SKUs must belong to the current account, while follower SKUs must belong to the follower account."
            ],
        )}
      </p>

      <FileDropZone
        onFileSelect={(file: File) => uploadMutation.mutate({ file })}
        disabled={followerOrgId === null || uploadMutation.isPending}
        label={
          followerOrgId === null ?
            t(($) => $.pricingv2["Select a follower account to enable uploading"])
          : t(($) => $.pricingv2["Drop the file to be imported here, or click the button below"])
        }
        isPending={uploadMutation.isPending}
        acceptedFileTypes={[".csv"]}
      />
    </VStack>
  );
}
